// SPDX-FileCopyrightText: 2026 Mads Ringblom
//
// SPDX-License-Identifier: GPL-3.0-or-later

import { useCallback, useRef } from 'react';

import { ApiRequestError, api } from '../../../api/client';
import type { ResourceKey, SurveyType } from '../../../api/types';
import type { CaseActions, CaseData } from '../../../app/bygning/useCaseData';
import { useCanEdit } from '../../../app/CaseRoleContext';
import { saveErrorMessage } from '../../../app/saveError';
import { useMutationQueue } from '../../../app/useMutationQueue';
import {
  BUILDUP_COLUMN_NAME,
  buildupColumnBody,
  buildupColumnKey,
  columnIdOf,
  hasServerMass,
  optionsWith,
  pickPlan,
  pickToast,
  serverPickPlan,
  serverPickToast,
  createFollowUp,
  splitLayerPlan,
  type Buildup,
} from '../../../bygning/buildups';
import { writeTargets } from '../../../bygning/fields';

/** The bits of the Opbygning column a write needs. */
interface ColumnRef {
  keyId: string;
  data_type: ResourceKey['data_type'];
  options: string[];
}

/**
 * The inspector's writes that are more than one `CaseActions` call: a
 * type-level resource value (every part, or the carrier), a build-up pick
 * and splitting a build-up layer off as a type. A pick is one `PATCH
 * /survey/types/{id} {buildup_id}` on a server that computes the tonnes
 * (ReUseX schema v37); on an older one it is the `Opbygning` user column
 * created on first use — decision 6 — then the value on every part, then
 * EAK/tons. Column writes (`POST /resources/columns`, a new select option) run
 * on the app write chain like every other write; the catalogue is re-read
 * after them (`actions.refresh`).
 */
export function useTypeWrites(data: CaseData, actions: CaseActions) {
  // TypeInspector hides the controls from a viewer; this refuses the write too.
  const canEdit = useCanEdit();
  const { mutate } = useMutationQueue({ onError: (cause) => actions.notify(saveErrorMessage(cause)) });
  // A column made in this session, until the catalogue re-read shows it.
  const made = useRef<ColumnRef | null>(null);

  /** A key's type-level value: one write per target, in order. Resolves when the last settles. */
  const commitKey = useCallback(
    async (type: SurveyType, key: ResourceKey, value: string | null) => {
      if (!canEdit) return;
      for (const code of writeTargets(type, key)) {
        const ok = await actions.patchResource(code, { [key.id]: value }, key.label);
        if (!ok) return;
      }
    },
    [actions, canEdit],
  );

  /** The Opbygning column, created on first use (a 409 means it exists already: look it up). */
  const ensureColumn = useCallback(async (): Promise<{ col: ColumnRef; created: boolean } | null> => {
    const known = buildupColumnKey(data.resourceKeys);
    if (known) return { col: { keyId: known.id, data_type: known.data_type, options: known.options }, created: false };
    if (made.current) return { col: made.current, created: false };
    const box: { col: ColumnRef | null } = { col: null };
    await mutate(async () => {
      try {
        const def = await api.createResourceColumn(buildupColumnBody());
        box.col = { keyId: `col:${def.id}`, data_type: 'enum', options: def.options ?? [] };
      } catch (cause) {
        if (!(cause instanceof ApiRequestError) || cause.status !== 409) throw cause;
        const list = await api.resourceColumns();
        const def = list.find((c) => c.name.trim().toLowerCase() === BUILDUP_COLUMN_NAME.toLowerCase());
        if (!def) throw cause;
        const dataType: ResourceKey['data_type'] =
          def.type === 'select' ? 'enum' : def.type === 'url' ? 'text' : (def.type as ResourceKey['data_type']);
        box.col = { keyId: `col:${def.id}`, data_type: dataType, options: def.options ?? [] };
      }
    });
    if (!box.col) return null;
    made.current = box.col;
    return { col: box.col, created: true };
  }, [data.resourceKeys, mutate]);

  /**
   * Picks a build-up (null clears it — only a server that keeps `buildup_id`
   * offers that): one PATCH there, the column writes otherwise; one toast.
   */
  const pickBuildup = useCallback(
    async (type: SurveyType, previous: Buildup | null, next: Buildup | null) => {
      if (!canEdit) return;
      if (hasServerMass(type)) {
        const plan = serverPickPlan(type, next);
        const updated = await actions.patchType(type.id, plan.patch);
        if (updated) actions.notify(serverPickToast(plan, updated));
        return;
      }
      if (!next) return;
      if (type.parts.length === 0) {
        actions.notify('Typen har ingen dele at gemme opbygningen på — tilføj en del først');
        return;
      }
      const ensured = await ensureColumn();
      if (!ensured) return;
      const { col } = ensured;
      let refresh = ensured.created;
      const opts = optionsWith(col, next.name);
      const colId = columnIdOf(col.keyId);
      if (opts && colId !== null) {
        const done = { ok: false };
        await mutate(async () => {
          await api.updateResourceColumn(colId, { options: opts });
          done.ok = true;
        });
        if (!done.ok) return;
        col.options = opts;
        refresh = true;
      }
      for (const p of [...type.parts].sort((a, b) => a.code.localeCompare(b.code))) {
        const ok = await actions.patchResource(p.code, { [col.keyId]: next.name }, BUILDUP_COLUMN_NAME);
        if (!ok) return;
      }
      const plan = pickPlan(type, previous, next);
      if (Object.keys(plan.patch).length > 0) {
        const ok = await actions.patchType(type.id, plan.patch);
        if (!ok) return;
      }
      actions.notify(pickToast(plan));
      if (refresh) actions.refresh();
    },
    [actions, canEdit, ensureColumn, mutate],
  );

  /** "Opret som egen type": the layer as a type with one manual part carrying the quantity and tonnes. */
  const splitLayer = useCallback(
    async (type: SurveyType, b: Buildup, index: number) => {
      const plan = canEdit ? splitLayerPlan(type, b, index) : null;
      if (!plan) return null;
      const created = await actions.createType(plan.create);
      if (!created) return null;
      const part = await actions.createResource({ type_id: created.id, name: plan.partName });
      if (part) {
        await actions.patchPart(part.code, { quantity: plan.quantity });
      }
      const followUp = createFollowUp(plan.create, created);
      if (followUp) await actions.patchType(created.id, followUp);
      actions.notify(`${plan.create.name} oprettet som egen type`);
      return created;
    },
    [actions, canEdit],
  );

  return { commitKey, pickBuildup, splitLayer };
}
